import { getLayoutTitle, normalizeTitleCandidate } from "connected-components/ai-teammate/helpers/pinned-artifact-title"
import {
  type ArtifactEnvelope,
  artifactKindLabel,
  isArtifactEnvelope,
  isFileArtifact,
  isMarkdownArtifact,
  isPlotArtifact,
  isTableArtifact,
  isTextArtifact,
  normalizeFileArtifact,
} from "./artifact-contract"

const LEADING_MARKDOWN_MARKERS = /^[\s>#*_`~-]+/
const TRAILING_MARKDOWN_MARKERS = /[\s*_`~#]+$/
const NON_PROSE_LINE_PREFIXES = ["|", "```", "~~~"]
const GENERIC_ARTIFACT_NAMES = new Set(["artifact", "table", "chart", "text", "markdown", "report", "ai table", "ai plot"])

function isProseLine(line: string): boolean {
  const trimmed = line.trim()
  return !NON_PROSE_LINE_PREFIXES.some((prefix) => trimmed.startsWith(prefix))
}

function findFirstMeaningfulLine(body: string): string | null {
  for (const line of body.split("\n")) {
    if (!isProseLine(line)) continue
    const stripped = line.replace(LEADING_MARKDOWN_MARKERS, "").replace(TRAILING_MARKDOWN_MARKERS, "")
    const candidate = normalizeTitleCandidate(stripped)
    if (candidate !== null) return candidate
  }
  return null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function getArtifactEnvelope(part: unknown): ArtifactEnvelope | null {
  if (!isRecord(part)) return null
  const { data } = part
  return isArtifactEnvelope(data) ? data : null
}

function getSiblingPlotTableArtifactId(plot: ArtifactEnvelope): string | null {
  const candidate = plot.tableArtifactId ?? plot.table_artifact_id
  return typeof candidate === "string" ? candidate : null
}

function findSiblingPlotTitle(tableArtifactId: string, parts: unknown[]): string | null {
  for (const part of parts) {
    const candidate = getArtifactEnvelope(part)
    if (candidate === null || !isPlotArtifact(candidate)) continue
    if (getSiblingPlotTableArtifactId(candidate) !== tableArtifactId) continue
    const layoutTitle = getLayoutTitle(candidate.figure.layout)
    if (layoutTitle !== null) return layoutTitle
  }
  return null
}

export function artifactCardName(artifact: ArtifactEnvelope, message?: { parts?: unknown[] }): string {
  if (isFileArtifact(artifact)) return normalizeFileArtifact(artifact).filename

  const backendName = normalizeTitleCandidate(artifact.name)
  if (backendName !== null && !GENERIC_ARTIFACT_NAMES.has(backendName.toLowerCase())) return backendName

  const explicitTitle = normalizeTitleCandidate(artifact.title)
  if (explicitTitle !== null && !GENERIC_ARTIFACT_NAMES.has(explicitTitle.toLowerCase())) return explicitTitle

  if (isPlotArtifact(artifact)) {
    const layoutTitle = getLayoutTitle(artifact.figure.layout)
    if (layoutTitle !== null) return layoutTitle
  }

  if (isTableArtifact(artifact)) {
    const siblingPlotTitle = findSiblingPlotTitle(artifact.id, Array.isArray(message?.parts) ? message.parts : [])
    if (siblingPlotTitle !== null) return `${siblingPlotTitle} (Table)`
  }

  if (isTextArtifact(artifact)) {
    const firstLine = findFirstMeaningfulLine(artifact.text)
    if (firstLine !== null) return firstLine
  }

  if (isMarkdownArtifact(artifact)) {
    const firstLine = findFirstMeaningfulLine(artifact.markdown)
    if (firstLine !== null) return firstLine
  }

  return artifactKindLabel(artifact)
}
